<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'AsyncCommand',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'completed']);

  const props = defineProps({
    ...userComponentProps,
    icon: {
      type: String,
      required: false,
    },
    text: {
      type: String,
      required: true,
    },
    operation: {
      type: String,
      required: true,
    },
    payload: {
      type: Function,
      required: true,
    },
    dropdownItem: {
      type: Boolean,
      required: false,
      default: false,
    },
    block: {
      type: Boolean,
      required: false,
      default: false,
    },
    variant: {
      type: String,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    size: {
      type: String,
      required: false,
      default: 'md',
    },
    tooltip: {
      type: String,
      required: false,
    },
    pollingInterval: {
      type: Number,
      required: false,
      default: 1000,
    },
    type: {
      type: String,
      required: false,
    },
    form: {
      type: null,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const beforeDestroy = (...args) => {
    {
      {
        poller.value && poller.value.stop();
      }
    }
  };
  const submit = (...args) => {
    const [event] = args;

    event.originalEvent.preventDefault();
    event.originalEvent.stopPropagation();

    if (poller.value.isRunning) {
      return false;
    }

    poller.value.begin(
      Promise.resolve(
        _.isFunction(props.payload) ? props.payload() : props.payload
      ).then((payload) => {
        if (!payload || payload.cancel) {
          return;
        }
        return $app.value.connections
          .request(props.operation, { content: payload })
          .then((resp) => resp.data);
      })
    );
  };

  onBeforeUnmount(beforeDestroy);

  const poller = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    poller: poller.value,
    ux: ux.value,
  }));

  const $$load_poller = () => {
    return new $app.value.helpers.AsyncCommandPoller(
      $app.value,
      handleError,
      (data) => emit('completed', data)
    );
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'poller' }, { name: 'ux' }],
    loaders: { poller: $$load_poller },
    ux: {},
    dataRefs: { poller, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.icon,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('icon');
    }
  );
  watch(
    () => props.text,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('text');
    }
  );
  watch(
    () => props.operation,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('operation');
    }
  );
  watch(
    () => props.dropdownItem,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('dropdownItem');
    }
  );
  watch(
    () => props.block,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('block');
    }
  );
  watch(
    () => props.variant,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('variant');
    }
  );
  watch(
    () => props.disabled,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('disabled');
    }
  );
  watch(
    () => props.size,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('size');
    }
  );
  watch(
    () => props.tooltip,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('tooltip');
    }
  );
  watch(
    () => props.pollingInterval,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('pollingInterval');
    }
  );
  watch(
    () => props.type,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('type');
    }
  );
  watch(
    () => props.form,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('form');
    }
  );

  const act_7315092382398562_script_64j7yfpvu_value_callable = async (
    $event
  ) => {
    return submit($event);
  };
  const act_7315092382398562_script_64j7yfpvu_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_7315092382398562_script_64j7yfpvu_value_callable($event);
      },
    };
  };

  const act_7315092382398562_script_64j7yfpvu = new actionTypes.ScriptAction({
    actionArgs: act_7315092382398562_script_64j7yfpvu_getActionArgs,
  });
  const inst_evh_7315092382398562_clickPreventStop_3i057w2dq =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_script_64j7yfpvu,
      event: 'click.prevent.stop',
      displayName: 'script',
    });
  const inst_evh_7315092382398562_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_7315092382398562_clickPreventStop_3i057w2dq],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_clickPreventStop_3i057w2dq,
    inst_evh_7315092382398562_clickPreventStop,
  };

  const evh_7315092382398562_clickPreventStop_3i057w2dq = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_7315092382398562_clickPreventStop_3i057w2dq.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpButton
    :type="type"
    :form="form"
    :text="text"
    :icon="icon"
    :variant="variant"
    :spinning="poller.isRunning"
    :disabled="disabled"
    :size="size"
    :tooltip="tooltip"
    :block="block"
    :dropdownItem="dropdownItem"
    dropdown-stays-open-on-click
    class="async-command"
    v-if="loaded"
    @click.prevent.stop="evh_7315092382398562_clickPreventStop($event, {})"
  >
  </ItpButton>
</template>
